/* eslint-disable @typescript-eslint/naming-convention */
import { colorsPalettes } from "@similarweb/styles";

import { TrendChangeTooltip } from "app/pages/activation/components/Shared/TrendChangeTooltip/TrendChangeTooltip";
import { ValueType } from "app/pages/activation/components/Shared/TrendChangeTooltip/type";
import type {
    KeywordShareSchema,
    NormalizedTopicSchema,
    TopicSchema,
    TopicTrendSchema } from "app/pages/activation/components/Topics/types";
import {
    PieChartId
} from "app/pages/activation/components/Topics/types";
import type { IWebsitesFavicons } from "app/services/sitesService/types";

import type { BenchmarkGranularity } from "../../types/common";

const COLORS = {
    PRIMARY: colorsPalettes.blue[400],
    LEADER: colorsPalettes.sky[300],
    OTHERS: colorsPalettes.carbon[50],
};

type TooltipItemData = {
    id: PieChartId;
    name: string;
    color: string;
    share: number;
    change?: number;
};

export const preparePieChart = ({
    leader,
    other,
    primary,
}: {
    leader: KeywordShareSchema;
    primary: KeywordShareSchema;
    other: { share: number };
}) => {
    const isPrimaryLeader = primary.domain === leader.domain;

    const tooltipData: Array<TooltipItemData> = [
        {
            id: PieChartId.Primary,
            name: `${primary.domain} (you)`,
            color: COLORS.PRIMARY,
            share: primary.shareOfVoice,
            change: primary.shareOfVoiceChange,
        },
        {
            id: PieChartId.Others,
            name: "activation.widget.topics.pieChart.others",
            color: COLORS.OTHERS,
            share: other.share,
        },
    ];

    if (!isPrimaryLeader) {
        tooltipData.splice(1, 0, {
            id: PieChartId.Leader,
            name: leader.domain,
            color: COLORS.LEADER,
            share: leader.shareOfVoice,
        });
    }

    const leaderChartSeries = {
        color: COLORS.LEADER,
        id: PieChartId.Leader,
        name: "Leader",
        total: 1,
        y: leader.shareOfVoice,
        tooltipData,
    };

    const primaryChartSeries = {
        color: COLORS.PRIMARY,
        id: PieChartId.Primary,
        name: "Primary",
        total: 1,
        y: primary.shareOfVoice,
        tooltipData,
    };

    const otherChartSeries = {
        color: COLORS.OTHERS,
        id: PieChartId.Others,
        name: "Others",
        total: 1,
        y: other.share,
        tooltipData,
    };

    return isPrimaryLeader
        ? [primaryChartSeries, otherChartSeries]
        : [leaderChartSeries, primaryChartSeries, otherChartSeries];
};

export const prepareTrendData = ({
    data,
    granularity,
    valueType = ValueType.Absolute,
}: {
    data: TopicTrendSchema[];
    granularity: BenchmarkGranularity;
    valueType?: ValueType;
}) => {
    return data.map((d) => ({
        value: d.value.value,
        tooltip: (
            <TrendChangeTooltip
                value={d?.value.value}
                change={d?.value.change}
                granularity={granularity}
                date={d.key}
                valueType={valueType}
            />
        ),
    }));
};

export const normalizeResponse = ({
    topicsRaw,
    icons,
    granularity,
}: {
    topicsRaw: TopicSchema[];
    icons?: IWebsitesFavicons;
    granularity: BenchmarkGranularity;
}): NormalizedTopicSchema[] =>
    topicsRaw.map((topic) => {
        return {
            id: topic?.topicId,
            isPreloading: !!topic?.isPreloading,
            keywordsCount: topic?.keywordsCount,
            name: topic?.topicName,
            shareOfVoice: topic?.primary.shareOfVoice,
            shareOfVoiceChange: topic?.primary.shareOfVoiceChange,
            primaryName: topic?.primary.domain,
            leaderName: topic?.leader?.domain,
            leaderShareOfVoice: topic?.leader?.shareOfVoice,
            leaderIcon: icons ? icons[topic?.leader?.domain] : null,
            volume: topic?.volume,
            volumeChange: topic?.volumeChange,
            created: new Date(topic?.created),
            trend: prepareTrendData({ data: topic?.trend, granularity }),
            isShared: topic?.isShared,
            sharedBy: topic?.sharedBy,
            pieChartData: preparePieChart({
                leader: topic?.leader,
                primary: topic?.primary,
                other: topic?.others,
            }),
        };
    });
